import { useMemo, useState } from 'react';
import {
  totalFor,
  type GeometryTotals,
  type MeasureBy,
} from '@obraplan/core/domain/takeoff/geometry';
import {
  acceptsSideMargin,
  APPLIES_TO_LABEL,
  isAreaMeasure,
  isPendingElement,
  isValidPair,
  MEASURE_SHORT_LABEL,
  measureUnit,
  measureLabel,
  measuresFor,
  SELECTABLE_ELEMENTS,
  serviceElement,
  type AppliesTo,
} from '@obraplan/core/domain/takeoff/applies-to';
import { serviceVolume } from '@obraplan/core/domain/takeoff/service';
import {
  hasImportedSheets,
  importedSheetNames,
  type CatalogService,
  type ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import {
  resolveServiceQuantity,
  typesInServiceUnit,
  unitLabel,
  type ResolvedServiceQuantity,
  type ServiceQuantityDecision,
} from '@obraplan/core/domain/takeoff/service-quantity';
import { withSteelRate } from '@obraplan/core/domain/takeoff/steel';
import type { UnitOfMeasure } from '@obraplan/core/domain/schedule/unit-of-measure';
import { asId, type UnitOfMeasureId } from '@obraplan/core/domain/shared/ids';
import { Pencil, RotateCcw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/feedback/states';
import { Field } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { NumeroOpcionalInput } from './campos';
import { CorrecaoComMotivo } from './CorrecaoComMotivo';
import { metros, numeroDaMedida } from './formato';
import {
  comAsPecas,
  EXPLICACAO_DAS_PECAS,
  pecasDosServicos,
  ROTULO_DAS_PECAS,
  type ServicoComPecas,
} from './pecas-importadas';
import { taxaDoServico } from './taxa-por-elemento';
import type { TypologyDraft, WorkspaceAction } from './typology-draft';

/**
 * O orçamento ao lado do traçado.
 *
 * É aqui que a geometria deixa de ser um desenho bonito e vira número conferível: para
 * cada serviço da planilha, a quantidade que **o contorno já determina** posta ao lado da
 * que a construtora orçou. As duas colunas não competem — a planilha é o que se fez, o
 * traçado é o que o desenho diz, e a diferença entre elas é a conversa que a fase inteira
 * existe para provocar.
 *
 * Duas coisas precisam ser ditas na tela, e estão:
 *
 * - **isto é custo direto, e não há preço aqui** (RN-173). Preço mora no sistema de
 *   compras e depende de Edge Function que ainda não existe; prender o quantitativo a
 *   ela seria não entregar nada;
 * - **a medida de cada serviço é palpite até alguém confirmar**. "M²" não distingue
 *   alvenaria de reboco de piso, e o palpite errado erra o quantitativo sem que nenhum
 *   número pareça estranho.
 *
 * Desde a Fase 24 a medida é um **par**: a peça ("Aplica a") e a grandeza ("Mede por"), nessa
 * ordem, porque a peça decide quais grandezas existem (RN-256). Volume de pilar e volume de
 * viga são duas escolhas, e não uma (RN-257).
 */

const SELECT_CLASS =
  'h-11 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9';

interface LinhaOrcamento {
  /** Com a taxa de aço da disciplina pendurada (Fase 27) — o que a medida "aço" lê. */
  readonly service: CatalogService & { readonly steelRatePerM3?: number };
  readonly disciplineName: string;
  /** O que a planilha orçou para a aba de referência. Ausente = a aba não tem o serviço. */
  readonly reference?: number;
  /** O que o contorno determina para esta tipologia. Ausente em serviço por unidade. */
  readonly traced?: number;
  readonly difference?: number;
  /** Diferença relativa, para a tela julgar a distância sem depender da ordem de grandeza. */
  readonly deviation?: number;
  readonly volume?: number;
  readonly building?: number;
}

function montarLinhas(
  catalog: ServiceCatalog,
  sheetName: string | undefined,
  tipologia: GeometryTotals,
  predio: GeometryTotals,
): readonly LinhaOrcamento[] {
  return catalog.disciplines.flatMap((disciplina) =>
    disciplina.services.map((doCatalogo): LinhaOrcamento => {
      // A taxa de aço da disciplina vai com o serviço (Fase 27, ADR-176): é o que a medida
      // "aço" multiplica pelo volume da peça — aqui, na tipologia e no prédio. Na disciplina que
      // mistura elementos, a do elemento do serviço (Fase 42, RN-573).
      const service = withSteelRate(doCatalogo, taxaDoServico(disciplina, doCatalogo));
      const traced = totalFor(service, tipologia);
      const reference = sheetName === undefined ? undefined : service.quantityBySheet[sheetName];
      const difference =
        traced === undefined || reference === undefined ? undefined : traced - reference;

      return {
        service,
        disciplineName: disciplina.name,
        ...(reference === undefined ? {} : { reference }),
        ...(traced === undefined ? {} : { traced }),
        ...(difference === undefined ? {} : { difference }),
        // Divisão por zero não é desvio infinito, é ausência de base para comparar.
        ...(difference === undefined || reference === undefined || reference === 0
          ? {}
          : { deviation: difference / reference }),
        ...(traced === undefined
          ? {}
          : {
              // A espessura só vira volume sobre uma área (fatia 10): "2 janelas" não têm camada.
              ...(!isAreaMeasure(service.measureBy) || serviceVolume(service, traced) === undefined
                ? {}
                : { volume: serviceVolume(service, traced)! }),
              building: totalFor(service, predio)!,
            }),
      };
    }),
  );
}

/**
 * Os dois números do serviço nesta tipologia e no prédio (Fase 24, fatia 5).
 *
 * Mora ao lado da linha, e não dentro dela: a linha é a comparação traçado × planilha, e
 * isto é a quantidade que o serviço **tem** — o traçado, a correção ou o digitado.
 */
interface QuantidadeDoServico {
  /** Com a parte que veio pronta das peças importadas (Fase 28), quando há. */
  readonly naTipologia: ServicoComPecas;
  readonly noPredio: ServicoComPecas;
  /** A unidade do cadastro (RN-232). Ausente = o serviço ainda não tem unidade. */
  readonly unidade?: UnitOfMeasure;
  /** O serviço já existe na obra — só então a unidade dele pode ser trocada. */
  readonly gravado: boolean;
}

/**
 * A decisão da tipologia, vezes os pavimentos — o número do prédio.
 *
 * O traçado do prédio já vem multiplicado (`predio`); o que alguém digitou para a tipologia
 * é multiplicado aqui, pela mesma conta: a tipologia acontece N vezes (D2).
 */
function vezes(
  decisao: ServiceQuantityDecision | undefined,
  floors: number,
): ServiceQuantityDecision | undefined {
  if (decisao === undefined) return undefined;
  return {
    ...(decisao.measured === undefined ? {} : { measured: decisao.measured * floors }),
    ...(decisao.reason === undefined ? {} : { reason: decisao.reason }),
    ...(decisao.quantity === undefined ? {} : { quantity: decisao.quantity * floors }),
  };
}

/**
 * O número na unidade do serviço, escrito. Peça contada é peça: sem casas — e a meia peça
 * da divisa (RN-163) aparece como meia, em vez de sumir no arredondamento.
 */
function naUnidade(resolvido: ResolvedServiceQuantity): string {
  const valor = resolvido.inServiceUnit ?? 0;
  if (resolvido.inServiceUnitOrigin !== 'CONTADO') return metros(valor);
  return metros(valor, Number.isInteger(valor) ? 0 : 1);
}

/** Perto o bastante para não valer conversa; longe o bastante para valer. */
function tomDoDesvio(deviation: number | undefined): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (deviation === undefined) return 'neutral';
  const distancia = Math.abs(deviation);
  if (distancia <= 0.01) return 'ok';
  if (distancia <= 0.1) return 'warn';
  return 'danger';
}

export function OrcamentoCard({
  catalog,
  draft,
  tipologia,
  predio,
  floors,
  dispatch,
  onAbaLigada,
  unidades = [],
  cadastroDoServico,
  onTrocarUnidade,
}: {
  catalog: ServiceCatalog | undefined;
  draft: TypologyDraft;
  tipologia: GeometryTotals;
  predio: GeometryTotals;
  /** Quantos pavimentos repetem esta tipologia — o que a coluna do prédio multiplica. */
  floors: number;
  dispatch: React.Dispatch<WorkspaceAction>;
  /** A aba acabou de ser ligada à tipologia — a página pergunta se marca o que ela orça. */
  onAbaLigada?: (aba: string) => void;
  /** O cadastro de unidades — as opções da unidade do serviço (D3). */
  unidades?: readonly UnitOfMeasure[];
  /**
   * O serviço como a obra o gravou, pela chave: é dele a unidade (RN-232). `undefined` é o
   * serviço que ainda não voltou do servidor — a unidade dele espera.
   */
  cadastroDoServico?: (
    serviceKey: string,
  ) => { readonly unitOfMeasureId?: UnitOfMeasureId } | undefined;
  /** Troca a unidade no cadastro. A recusa (medição gravada) volta dita por quem chama. */
  onTrocarUnidade?: (serviceKey: string, unitOfMeasureId: UnitOfMeasureId | undefined) => void;
}) {
  const [soComGeometria, setSoComGeometria] = useState(false);
  /**
   * Há planilha para comparar?
   *
   * Sem ela este card não é uma comparação — é **o quantitativo que o desenho determina**,
   * e só. Manter a coluna "Planilha", a coluna "Diferença" e o seletor de aba numa obra
   * montada dentro do sistema seria a tela perguntando por algo que não existe, e
   * respondendo com um travessão em cada linha.
   */
  const comparaComPlanilha = catalog !== undefined && hasImportedSheets(catalog);

  const linhas = useMemo(
    () => (catalog === undefined ? [] : montarLinhas(catalog, draft.sheetName, tipologia, predio)),
    [catalog, draft.sheetName, tipologia, predio],
  );

  const quantidades = useMemo(() => {
    const unidadePorId = new Map(unidades.map((unidade) => [unidade.id, unidade]));
    // As peças da planilha por peça somam ao traçado das outras (Fase 28, RN-384) — a mesma
    // conta da lista de itens, para o medido daqui e o de lá serem o mesmo número.
    const dasPecas = pecasDosServicos(draft.pieceQuantities);
    return new Map(
      linhas.map((linha): [string, QuantidadeDoServico] => {
        const cadastro = cadastroDoServico?.(linha.service.key);
        const unidade =
          cadastro?.unitOfMeasureId === undefined
            ? undefined
            : unidadePorId.get(cadastro.unitOfMeasureId);
        const decisao = draft.serviceQuantities?.[linha.service.key];
        const comum = {
          service: linha.service,
          ...(unidade === undefined ? {} : { serviceUnitCode: unidade.code }),
        };
        const noPredio = vezes(decisao, floors);
        const nasPecas = dasPecas[linha.service.key];
        return [
          linha.service.key,
          {
            naTipologia: comAsPecas(
              resolveServiceQuantity({
                ...comum,
                totals: tipologia,
                ...(decisao === undefined ? {} : { decision: decisao }),
              }),
              nasPecas,
            ),
            // A peça acontece tantas vezes quanto a tipologia, como o digitado (D2).
            noPredio: comAsPecas(
              resolveServiceQuantity({
                ...comum,
                totals: predio,
                ...(noPredio === undefined ? {} : { decision: noPredio }),
              }),
              nasPecas === undefined ? undefined : nasPecas * floors,
            ),
            gravado: cadastro !== undefined,
            ...(unidade === undefined ? {} : { unidade }),
          },
        ];
      }),
    );
  }, [
    linhas,
    unidades,
    cadastroDoServico,
    draft.serviceQuantities,
    draft.pieceQuantities,
    tipologia,
    predio,
    floors,
  ]);
  const corrigidos = [...quantidades.values()].filter(
    (quantidade) => quantidade.naTipologia.measuredOrigin === 'CORRIGIDO',
  ).length;

  const visiveis = soComGeometria ? linhas.filter((linha) => linha.traced !== undefined) : linhas;
  const determinados = linhas.filter((linha) => linha.traced !== undefined).length;
  const longe = linhas.filter((linha) => tomDoDesvio(linha.deviation) === 'danger').length;
  const confirmados = linhas.filter((linha) => linha.service.measureOrigin === 'DECIDIDO').length;
  const semElemento = linhas.filter((linha) => isPendingElement(linha.service)).length;

  return (
    <Card>
      <CardHeader>
        {/*
          Não se chama "quantitativo": o quantitativo é lançado item a item na aba de
          Itens e consolidação. O que esta tabela faz é dizer, para cada serviço, **quanto
          o desenho determina dele** — a base de onde o lançamento parte.
        */}
        <CardTitle>{comparaComPlanilha ? 'Orçamento × traçado' : 'Serviços × traçado'}</CardTitle>
        <CardDescription>
          {comparaComPlanilha ? (
            <>
              Para cada serviço, a quantidade que o contorno já determina — área de parede, de face,
              de piso — ao lado da que a construtora orçou na planilha.
            </>
          ) : (
            <>
              Para cada serviço cadastrado, a quantidade que o contorno já determina — área de
              parede, de face, de piso. Importar a planilha do orçamento acrescenta a coluna do que
              a construtora orçou e a diferença entre as duas.
            </>
          )}{' '}
          <strong className="font-medium text-(--color-content)">
            Isto é custo direto, e não há preço nesta fase
          </strong>{' '}
          (RN-173): preço vem do sistema de compras, e tem versão própria.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {catalog === undefined ? (
          <EmptyState
            title="Nenhum serviço cadastrado"
            description="Cadastre a disciplina e os serviços dela acima — ou importe a planilha, que preenche o cadastro de uma vez. Sem serviço, a geometria calcula áreas que não têm a quem pertencer."
          />
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-4">
              {comparaComPlanilha && (
                <Field
                  label={`Aba que orça ${draft.name}`}
                  htmlFor="orcamento-aba"
                  hint="A comparação é aba contra tipologia: a soma das oito não é comparável com um desenho."
                >
                  <select
                    id="orcamento-aba"
                    className={SELECT_CLASS}
                    value={draft.sheetName ?? ''}
                    onChange={(event) => {
                      dispatch({
                        type: 'SET_TYPOLOGY_SHEET',
                        unitTypeId: draft.unitTypeId,
                        sheetName: event.target.value,
                      });
                      if (event.target.value !== '') onAbaLigada?.(event.target.value);
                    }}
                  >
                    <option value="">— sem referência —</option>
                    {importedSheetNames(catalog).map((nome) => (
                      <option key={nome} value={nome}>
                        {nome}
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              <label className="flex h-11 items-center gap-2 text-sm md:h-9">
                <input
                  type="checkbox"
                  checked={soComGeometria}
                  onChange={(event) => setSoComGeometria(event.target.checked)}
                />
                Só os serviços que a geometria determina
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge tone="info">
                {determinados} de {linhas.length} serviços saem do desenho
              </Badge>
              {comparaComPlanilha && draft.sheetName !== undefined && longe > 0 && (
                <Badge tone="danger">{longe} passam de 10% de diferença</Badge>
              )}
              <Badge tone={confirmados === linhas.length ? 'ok' : 'neutral'}>
                {confirmados} de {linhas.length} com a medida confirmada
              </Badge>
              {/*
                Volume e espala sem peça não medem nada (D17, RN-260). Contá-los aqui é o que faz
                alguém procurá-los: na tabela, são linhas com travessão, iguais às de unidade.
              */}
              {semElemento > 0 && (
                <Badge tone="warn">
                  {semElemento === 1
                    ? '1 pede o elemento'
                    : `${String(semElemento)} pedem o elemento`}
                </Badge>
              )}
              {corrigidos > 0 && (
                <Badge tone="info">
                  {corrigidos === 1
                    ? '1 medido corrigido à mão'
                    : `${String(corrigidos)} medidos corrigidos à mão`}
                </Badge>
              )}
              <span className="text-(--color-content-muted)">
                “M²” não distingue alvenaria de reboco de piso, então a medida nasce de palpite pelo
                nome do serviço — e o palpite é refeito a cada importação. O que você escolher aqui
                passa a mandar, e a planilha reimportada não mexe mais nele.
              </span>
            </div>

            <TableWrapper className="max-h-[32rem] overflow-y-auto">
              <Table>
                <TableHeader sticky>
                  <TableRow>
                    <TableHead className="min-w-64">Serviço</TableHead>
                    <TableHead className="w-40">Aplica a</TableHead>
                    <TableHead className="w-64">Mede por</TableHead>
                    <TableHead className="w-32">Espessura</TableHead>
                    {comparaComPlanilha && <TableHead className="text-right">Planilha</TableHead>}
                    <TableHead className="text-right">Traçado</TableHead>
                    {comparaComPlanilha && <TableHead className="text-right">Diferença</TableHead>}
                    {/*
                      A quantidade do serviço (Fase 24, fatia 5): o medido — o traçado, a
                      correção com motivo, ou o digitado — e o mesmo número na unidade do
                      cadastro, que é a da meta e da medição do planejamento.
                    */}
                    <TableHead className="min-w-40 text-right">Medido</TableHead>
                    <TableHead className="w-32">Unidade</TableHead>
                    <TableHead className="min-w-36 text-right">Na unidade</TableHead>
                    <TableHead className="text-right">Volume</TableHead>
                    <TableHead className="text-right">
                      No prédio
                      <span className="block text-xs font-normal text-(--color-content-muted)">
                        × {floors}
                      </span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visiveis.map((linha) => (
                    <LinhaDoServico
                      key={linha.service.key}
                      linha={linha}
                      comparaComPlanilha={comparaComPlanilha}
                      dispatch={dispatch}
                      quantidade={quantidades.get(linha.service.key)!}
                      unitTypeId={draft.unitTypeId}
                      unidades={unidades}
                      {...(onTrocarUnidade === undefined ? {} : { onTrocarUnidade })}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableWrapper>

            {visiveis.length === 0 && (
              <p className="text-sm text-(--color-content-muted)">
                Nenhum serviço com medida ligada à geometria. Escolha “mede por” em pelo menos um
                deles.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * O número do traçado com a unidade da grandeza ao lado — "3,48 m²", "2 un", "8,80 m".
 *
 * A unidade é um elemento à parte, e não parte do texto do número: quem lê a célula vê as
 * duas coisas, e quem a procura pelo número o acha. Sem ela, a coluna assumia m² em tudo —
 * e a contagem de janelas, o contorno delas e a área do vão eram o mesmo "2,00".
 */
function NumeroComUnidade({
  valor,
  measureBy,
  appliesTo,
}: {
  valor: number;
  measureBy: MeasureBy;
  /** A borda do radier e da laje é m², e não m (Fase 27, etapa 3): a unidade sai do par. */
  appliesTo: AppliesTo | undefined;
}) {
  const unidade = measureUnit(appliesTo, measureBy);
  return (
    <>
      {numeroDaMedida(valor, measureBy)}
      {unidade !== '' && (
        <span className="ml-1 text-xs text-(--color-content-muted)">{unidade}</span>
      )}
    </>
  );
}

function LinhaDoServico({
  linha,
  comparaComPlanilha,
  dispatch,
  quantidade,
  unitTypeId,
  unidades,
  onTrocarUnidade,
}: {
  linha: LinhaOrcamento;
  comparaComPlanilha: boolean;
  dispatch: React.Dispatch<WorkspaceAction>;
  quantidade: QuantidadeDoServico;
  unitTypeId: TypologyDraft['unitTypeId'];
  unidades: readonly UnitOfMeasure[];
  onTrocarUnidade?: (serviceKey: string, unitOfMeasureId: UnitOfMeasureId | undefined) => void;
}) {
  const { service } = linha;
  const tom = tomDoDesvio(linha.deviation);

  /**
   * A peça escolhida que ainda espera a medida (RN-260).
   *
   * Trocar para um elemento que não aceita a medida de hoje não grava nada — o par ficaria
   * inválido e não mediria coisa alguma. A escolha espera aqui, na linha, e vai junto com a
   * medida quando ela chegar.
   */
  const [emEscolha, setEmEscolha] = useState<AppliesTo | undefined>(undefined);
  const gravado = serviceElement(service);
  const elemento = emEscolha ?? gravado;
  const medidas = elemento === undefined ? [service.measureBy] : measuresFor(elemento);

  return (
    <TableRow>
      <TableCell>
        <span className="block font-medium">{service.name}</span>
        <span className="text-xs text-(--color-content-muted)">
          {linha.disciplineName}
          {service.unit === undefined ? '' : ` · ${service.unit}`} ·{' '}
          {service.items.length === 1 ? '1 item' : `${String(service.items.length)} itens`}
        </span>
      </TableCell>

      <TableCell>
        <select
          className={`${SELECT_CLASS} w-full min-w-32`}
          aria-label={`A que ${service.name} se aplica`}
          value={elemento ?? ''}
          onChange={(event) => {
            const escolhido = event.target.value as AppliesTo;
            // A medida de hoje serve à peça nova: o par vai inteiro, e mais nada a pedir.
            if (isValidPair(escolhido, service.measureBy)) {
              setEmEscolha(undefined);
              dispatch({
                type: 'SET_SERVICE_MEASURE',
                serviceKey: service.key,
                measureBy: service.measureBy,
                appliesTo: escolhido,
              });
              return;
            }
            setEmEscolha(escolhido);
          }}
        >
          {elemento === undefined && (
            <option value="" disabled>
              — escolha —
            </option>
          )}
          {SELECTABLE_ELEMENTS.map((opcao) => (
            <option key={opcao} value={opcao}>
              {APPLIES_TO_LABEL[opcao].toLocaleLowerCase('pt-BR')}
            </option>
          ))}
        </select>
        {gravado === undefined && emEscolha === undefined && (
          <span className="mt-1 block text-xs font-medium text-(--color-state-warn)">
            escolha o elemento
          </span>
        )}
      </TableCell>

      <TableCell>
        <select
          // Larga o bastante para a conta: "faces (2 laterais + fundo) × comprimento, m²".
          className={`${SELECT_CLASS} w-full min-w-56`}
          aria-label={`Como ${service.name} mede`}
          value={emEscolha === undefined ? service.measureBy : ''}
          // Sem peça, a grandeza não tem de onde sair: primeiro "Aplica a", depois isto.
          disabled={elemento === undefined}
          onChange={(event) => {
            setEmEscolha(undefined);
            dispatch({
              type: 'SET_SERVICE_MEASURE',
              serviceKey: service.key,
              measureBy: event.target.value as MeasureBy,
              ...(elemento === undefined ? {} : { appliesTo: elemento }),
            });
          }}
        >
          {emEscolha !== undefined && (
            <option value="" disabled>
              — escolha —
            </option>
          )}
          {/* O texto do par, com a conta e a unidade: "faces (4 × altura), m²" (fatia 10). */}
          {medidas.map((kind) => (
            <option key={kind} value={kind}>
              {elemento === undefined ? MEASURE_SHORT_LABEL[kind] : measureLabel(elemento, kind)}
            </option>
          ))}
        </select>
        {/*
          Dizer quais ainda são palpite é o que torna a distinção útil: sem isso, quem
          confere não sabe por onde começar, e as noventa linhas parecem igualmente
          prontas.
        */}
        {emEscolha !== undefined ? (
          <span className="mt-1 block text-xs font-medium text-(--color-state-warn)">
            escolha a medida
          </span>
        ) : (
          service.measureOrigin === 'PALPITE' && (
            <span className="mt-1 block text-xs text-(--color-content-muted)">palpite</span>
          )
        )}
        {/*
          A margem da soleira e do peitoril (RN-308), logo abaixo da medida que a usa: só na
          largura de porta, janela e passagem, como a espessura só vira volume sobre área. O
          número do traçado ao lado já sai com ela — Σ (largura + 2 × margem) × quantos.
        */}
        {emEscolha === undefined && acceptsSideMargin(gravado, service.measureBy) && (
          <label className="mt-1 flex items-center gap-1 text-xs text-(--color-content-muted)">
            margem para cada lado
            <NumeroOpcionalInput
              aria-label={`Margem de ${service.name} para cada lado, em metros`}
              placeholder="0"
              className="h-8 w-20 text-right"
              value={service.sideMargin}
              onChange={(sideMargin) =>
                dispatch({
                  type: 'SET_SERVICE_SIDE_MARGIN',
                  serviceKey: service.key,
                  ...(sideMargin === undefined ? {} : { sideMargin }),
                })
              }
            />
            m
          </label>
        )}
      </TableCell>

      <TableCell>
        <NumeroOpcionalInput
          aria-label={`Espessura de ${service.name}, em metros`}
          placeholder="—"
          className="h-9 w-24 text-right"
          value={service.layerThickness}
          onChange={(layerThickness) =>
            dispatch({
              type: 'SET_SERVICE_THICKNESS',
              serviceKey: service.key,
              ...(layerThickness === undefined ? {} : { layerThickness }),
            })
          }
        />
      </TableCell>

      {comparaComPlanilha && (
        <TableCell className="tabular text-right">
          {linha.reference === undefined ? (
            <span className="text-(--color-content-muted)">—</span>
          ) : (
            metros(linha.reference)
          )}
        </TableCell>
      )}

      <TableCell className="tabular text-right">
        {linha.traced === undefined ? (
          <span className="text-(--color-content-muted)">
            {gravado === undefined
              ? 'não mede nada'
              : // O aço da peça sem taxa na disciplina (Fase 27): a peça está lá, a conta não.
                service.measureBy === 'ACO' &&
                  service.steelRatePerM3 === undefined &&
                  serviceElement(service) !== 'ESTACA'
                ? 'sem taxa de aço'
                : 'não sai do desenho'}
          </span>
        ) : (
          <NumeroComUnidade
            valor={linha.traced}
            measureBy={service.measureBy}
            appliesTo={serviceElement(service)}
          />
        )}
      </TableCell>

      {comparaComPlanilha && (
        <TableCell className="tabular text-right">
          {linha.difference === undefined ? (
            <span className="text-(--color-content-muted)">—</span>
          ) : (
            <Badge tone={tom}>
              {linha.difference > 0 ? '+' : ''}
              {metros(linha.difference)}
              {linha.deviation === undefined
                ? ''
                : ` · ${linha.deviation > 0 ? '+' : ''}${metros(linha.deviation * 100, 1)}%`}
            </Badge>
          )}
        </TableCell>
      )}

      <TableCell className="text-right align-top">
        <MedidoDoServico
          service={service}
          resolvido={quantidade.naTipologia}
          unitTypeId={unitTypeId}
          dispatch={dispatch}
        />
      </TableCell>

      <TableCell className="align-top">
        <UnidadeDoServico
          service={service}
          quantidade={quantidade}
          unidades={unidades}
          {...(onTrocarUnidade === undefined ? {} : { onTrocarUnidade })}
        />
      </TableCell>

      <TableCell className="text-right align-top">
        <NaUnidadeDoServico
          service={service}
          quantidade={quantidade}
          unitTypeId={unitTypeId}
          dispatch={dispatch}
        />
      </TableCell>

      <TableCell className="tabular text-right">
        {linha.volume === undefined ? (
          <span className="text-(--color-content-muted)">—</span>
        ) : (
          `${metros(linha.volume, 3)} m³`
        )}
      </TableCell>

      <TableCell className="tabular text-right">
        {/*
          O prédio é o medido vezes os pavimentos (D2) — e não só o traçado: a correção e o
          número digitado também se repetem em cada andar.
        */}
        {quantidade.noPredio.measured === undefined ? (
          <span className="text-(--color-content-muted)">—</span>
        ) : (
          <NumeroComUnidade
            valor={quantidade.noPredio.measured}
            measureBy={service.measureBy}
            appliesTo={serviceElement(service)}
          />
        )}
        {quantidade.noPredio.unitsDiverge &&
          quantidade.noPredio.inServiceUnit !== undefined &&
          quantidade.unidade !== undefined && (
            <span className="block text-xs text-(--color-content-muted)">
              {naUnidade(quantidade.noPredio)} {quantidade.unidade.name}
            </span>
          )}
      </TableCell>
    </TableRow>
  );
}

// ── A quantidade do serviço (Fase 24, fatia 5) ──────────────────────────────────

/**
 * O medido: o número do serviço nesta tipologia, e a base do índice de consumo.
 *
 * Três jeitos de existir, e a célula diz qual é:
 *
 * - **o desenho mede** e ninguém discordou: segue o traçado, que já está na coluna ao lado,
 *   e o botão "Corrigir" abre a correção — que só aplica com motivo (D4);
 * - **corrigido**: o número de quem corrigiu, a marca, o traçado de hoje ao lado (de → para)
 *   e o botão que volta ao traçado. O desenho redesenhado depois não mexe na correção — ele
 *   aparece ao lado, e é assim que a correção envelhecida se denuncia (D1);
 * - **o desenho não mede** (unidade, modelo): o campo é a quantidade, e não pede motivo — ele
 *   não discorda de nada.
 *
 * A planilha por peça (Fase 28) acrescenta um quarto: o número que veio **pronto** das peças
 * importadas. Ele soma ao traçado das outras peças e aparece com a marca dele; o digitado e a
 * correção continuam vencendo, e as peças ficam ao lado, como o traçado fica ao lado da
 * correção.
 */
function MedidoDoServico({
  service,
  resolvido,
  unitTypeId,
  dispatch,
}: {
  service: CatalogService;
  resolvido: ServicoComPecas;
  unitTypeId: TypologyDraft['unitTypeId'];
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  const [corrigindo, setCorrigindo] = useState(false);
  const unidade =
    resolvido.measureUnitCode === undefined ? undefined : unitLabel(resolvido.measureUnitCode);
  const dasPecas = resolvido.fromPieces;

  if (resolvido.measuring === 'PENDENTE') {
    // Sem elemento o desenho não mede — mas a peça importada traz o número pronto (D3).
    if (dasPecas !== undefined) return <NumeroDasPecas resolvido={resolvido} />;
    return <span className="text-xs text-(--color-content-muted)">sem elemento, sem número</span>;
  }

  if (resolvido.measuring === 'DIGITADO') {
    // O campo é do digitado: com as peças e sem digitado, ele nasce vazio e as peças ficam à vista
    // em cima — preenchê-lo é decidir por cima delas.
    const digitado = resolvido.measuredOrigin === 'DIGITADO';
    return (
      <div className="flex flex-col items-end gap-0.5">
        {dasPecas !== undefined && !digitado && <NumeroDasPecas resolvido={resolvido} />}
        <NumeroOpcionalInput
          aria-label={`Quantidade de ${service.name} nesta tipologia`}
          placeholder={dasPecas === undefined ? 'digite' : 'digitar por cima'}
          className="ml-auto h-9 w-28 text-right"
          value={dasPecas !== undefined && !digitado ? undefined : resolvido.measured}
          onChange={(measured) =>
            dispatch(
              measured === undefined
                ? { type: 'CLEAR_SERVICE_MEASURED', unitTypeId, serviceKey: service.key }
                : { type: 'SET_SERVICE_MEASURED', unitTypeId, serviceKey: service.key, measured },
            )
          }
        />
        {dasPecas !== undefined && digitado && (
          <span
            className="tabular text-xs text-(--color-content-muted)"
            title={EXPLICACAO_DAS_PECAS}
          >
            {ROTULO_DAS_PECAS} {metros(dasPecas)} — o digitado vale
          </span>
        )}
      </div>
    );
  }

  if (corrigindo) {
    return (
      <CorrecaoComMotivo
        rotulo={`Medido corrigido de ${service.name}`}
        inicial={resolvido.measured}
        {...(resolvido.reason === undefined ? {} : { motivoInicial: resolvido.reason })}
        {...(unidade === undefined ? {} : { unidade })}
        onCancelar={() => setCorrigindo(false)}
        onAplicar={(measured, reason) => {
          dispatch({
            type: 'SET_SERVICE_MEASURED',
            unitTypeId,
            serviceKey: service.key,
            measured,
            reason,
          });
          setCorrigindo(false);
        }}
      />
    );
  }

  const corrigido = resolvido.measuredOrigin === 'CORRIGIDO';
  return (
    <div className="flex flex-col items-end gap-0.5">
      {corrigido ? (
        <>
          <span className="tabular flex items-center gap-1.5 font-medium">
            <Badge tone="info">corrigido</Badge>
            {metros(resolvido.measured!)}
          </span>
          <span className="tabular text-xs text-(--color-content-muted)">
            traçado {resolvido.traced === undefined ? '—' : metros(resolvido.traced)} →{' '}
            {metros(resolvido.measured!)}
          </span>
          {resolvido.reason !== undefined && (
            <span className="max-w-48 text-xs text-(--color-content-muted)">
              “{resolvido.reason}”
            </span>
          )}
          {dasPecas !== undefined && (
            <span
              className="tabular text-xs text-(--color-content-muted)"
              title={EXPLICACAO_DAS_PECAS}
            >
              {ROTULO_DAS_PECAS} {metros(dasPecas)} — a correção vale
            </span>
          )}
        </>
      ) : dasPecas !== undefined ? (
        <NumeroDasPecas resolvido={resolvido} />
      ) : (
        <span className="text-xs text-(--color-content-muted)">segue o traçado</span>
      )}
      <span className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Corrigir o medido de ${service.name}`}
          onClick={() => setCorrigindo(true)}
        >
          <Pencil className="size-4" />
        </Button>
        {corrigido && (
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Voltar ${service.name} ao traçado`}
            onClick={() =>
              dispatch({ type: 'CLEAR_SERVICE_MEASURED', unitTypeId, serviceKey: service.key })
            }
          >
            <RotateCcw className="size-4" />
          </Button>
        )}
      </span>
    </div>
  );
}

/**
 * O medido que veio, todo ou em parte, pronto das peças importadas (Fase 28, RN-384, RN-388).
 *
 * O número com a marca, e as duas partes embaixo quando o desenho também mede alguma coisa:
 * "traçado 2,40 + peças 114,08". O traçado já não conta as peças importadas, e é
 * por isso que somar não conta duas vezes.
 */
function NumeroDasPecas({ resolvido }: { resolvido: ServicoComPecas }) {
  const doDesenho = resolvido.traced ?? 0;
  return (
    <span className="flex flex-col items-end gap-0.5" title={EXPLICACAO_DAS_PECAS}>
      <span className="tabular flex items-center gap-1.5 font-medium">
        <Badge tone="info">{ROTULO_DAS_PECAS}</Badge>
        {metros(resolvido.measured ?? 0)}
      </span>
      {doDesenho > 0 && (
        <span className="tabular text-xs text-(--color-content-muted)">
          traçado {metros(doDesenho)} + peças {metros(resolvido.fromPieces ?? 0)}
        </span>
      )}
    </span>
  );
}

/**
 * A unidade do serviço — uma só, a do cadastro (D3), e não travada pelo "Mede por".
 *
 * Trocar aqui troca o cadastro, e é por isso que não vai para a bancada: a troca pode ser
 * recusada (medição gravada), e quem chama diz a recusa. O serviço que ainda não voltou do
 * servidor espera — não há de quem trocar a unidade.
 */
function UnidadeDoServico({
  service,
  quantidade,
  unidades,
  onTrocarUnidade,
}: {
  service: CatalogService;
  quantidade: QuantidadeDoServico;
  unidades: readonly UnitOfMeasure[];
  onTrocarUnidade?: (serviceKey: string, unitOfMeasureId: UnitOfMeasureId | undefined) => void;
}) {
  const ativas = unidades.filter(
    (unidade) => unidade.isActive || unidade.id === quantidade.unidade?.id,
  );
  return (
    <>
      <select
        className={`${SELECT_CLASS} w-full min-w-20`}
        aria-label={`Unidade de ${service.name}`}
        value={quantidade.unidade?.id ?? ''}
        disabled={onTrocarUnidade === undefined || !quantidade.gravado}
        onChange={(event) =>
          onTrocarUnidade?.(
            service.key,
            event.target.value === '' ? undefined : asId<'UnitOfMeasure'>(event.target.value),
          )
        }
      >
        <option value="">—</option>
        {ativas.map((unidade) => (
          <option key={unidade.id} value={unidade.id}>
            {unidade.name}
          </option>
        ))}
      </select>
      {quantidade.naTipologia.unitsDiverge && quantidade.naTipologia.measureUnitCode && (
        <span className="mt-1 block text-xs text-(--color-content-muted)">
          medida em {unitLabel(quantidade.naTipologia.measureUnitCode)}
        </span>
      )}
    </>
  );
}

/**
 * O número na unidade do serviço — o da meta e da medição do planejamento (D5).
 *
 * Igual ao medido quando as unidades coincidem, e a célula só diz isso; contado na planta no
 * pilar e na viga em "un"; digitado nas outras divergências.
 */
function NaUnidadeDoServico({
  service,
  quantidade,
  unitTypeId,
  dispatch,
}: {
  service: CatalogService;
  quantidade: QuantidadeDoServico;
  unitTypeId: TypologyDraft['unitTypeId'];
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  const resolvido = quantidade.naTipologia;
  if (!resolvido.unitsDiverge) {
    return <span className="text-xs text-(--color-content-muted)">= medido</span>;
  }

  if (typesInServiceUnit(resolvido)) {
    return (
      <NumeroOpcionalInput
        aria-label={`Quantidade de ${service.name} em ${quantidade.unidade?.name ?? 'unidade do serviço'}`}
        placeholder="digite"
        className="ml-auto h-9 w-28 text-right"
        value={resolvido.inServiceUnit}
        onChange={(quantity) =>
          dispatch({
            type: 'SET_SERVICE_QUANTITY',
            unitTypeId,
            serviceKey: service.key,
            ...(quantity === undefined ? {} : { quantity }),
          })
        }
      />
    );
  }

  return (
    <span className="tabular">
      {resolvido.inServiceUnit === undefined ? '—' : naUnidade(resolvido)}{' '}
      {quantidade.unidade?.name ?? ''}
      <span className="block text-xs text-(--color-content-muted)">peças contadas na planta</span>
    </span>
  );
}
